/**
 * Cibles (Targets) — shared contract for the targets service.
 * Design: docs/plans/targets/README.md + 01-data-model.md + 02-matching.md + 03-service-api.md
 *
 * Three layers: Target (UI object) → Field (one input) → Signal (matchable string).
 * The matcher never receives a Field; signals are compiled once at consolidation and never re-folded.
 *
 * UI label ≠ code name: the tab says « Cibles », code says `targets`.
 * The « Catalogue » tab is the `produit` category in bulk — no `catalogue` category exists.
 */

// ---------------------------------------------------------------------------
// Core enumeration types
// ---------------------------------------------------------------------------

export type TargetCategory =
  | 'marque'
  | 'offre'
  | 'produit'      // individual product pages / SKUs
  | 'collection'   // category / collection / section pages
  | 'contenu'
  | 'etablissement'
  | 'personne';

/**
 * D53 (Conversations) — canonical ordered 7-tuple of TargetCategory slugs.
 * The INDEX of every `…ByCategory` array in the Conversations feature: element-wise
 * addition is only valid when every writer uses this exact order. A new category is
 * an APPEND (never an insert). Import from `@traaker/shared`; NEVER re-declare.
 */
export const TARGET_CATEGORIES = [
  'marque',
  'offre',
  'produit',
  'collection',
  'contenu',
  'etablissement',
  'personne',
] as const satisfies readonly TargetCategory[];

/**
 * GeoZone granularity types — derived from Geoapify's result_type.
 * Unknown result_types degrade to 'locality' rather than being dropped (§4.5).
 */
export type GeoZoneType =
  | 'country'
  | 'region'
  | 'city'
  | 'village'
  | 'locality'
  | 'postcode'
  | 'address';

// ---------------------------------------------------------------------------
// Palette (D45) — a token the `marque` category uses for UI highlighting.
// Never a signal; never changes targetsHash. Token validates in the API.
// ---------------------------------------------------------------------------

export const TARGET_COLOR_PALETTE = [
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
] as const;

export type TargetColorToken = (typeof TARGET_COLOR_PALETTE)[number];

// ---------------------------------------------------------------------------
// Zone — an embedded value object (D16, §2.4)
// NOT a zoneId reference; no shared zone table.
// ---------------------------------------------------------------------------

/**
 * Embedded zone on an `etablissement`. No separate GeoZone table (D16).
 * countryCode is required — E.164 normalization needs it.
 * latitude/longitude resolved async via geo-batch; coordinates are for the future map view.
 */
export interface TargetZone {
  label: string;
  type: GeoZoneType;
  /** ISO-3166-1 alpha-2, uppercase. Required. */
  countryCode: string;
  cityName?: string;
  regionName?: string;
  postcode?: string;
  address?: string;
  /** Resolved async by geo-batch (D32). Present once geocodeStatus = 'ok'. */
  latitude?: number;
  longitude?: number;
  /** Provenance only — never a key, never a signal (§2.4). */
  providerPlaceId?: string;
  geocodeStatus: 'ok' | 'pending' | 'failed';
}

// ---------------------------------------------------------------------------
// GeoSuggestion — what the autocomplete returns (§4.5).
// Provider payload never reaches the client; swap provider → change one file.
// ---------------------------------------------------------------------------

export interface GeoSuggestion {
  /** Display text shown in the dropdown. */
  label: string;
  type: GeoZoneType;
  countryCode: string;
  cityName?: string;
  regionName?: string;
  postcode?: string;
  address?: string;
  /** On selection: no second geocoding call, everything is already here. */
  latitude?: number;
  longitude?: number;
  providerPlaceId?: string;
}

// ---------------------------------------------------------------------------
// PlaceSearchSuggestion + PlaceDetails — Google Places business lookup (§4.6).
// Returned by GET /locations/place-search and GET /locations/place-details.
// ---------------------------------------------------------------------------

/** One result from GET /locations/place-search (Google Places Autocomplete). */
export interface PlaceSearchSuggestion {
  placeId: string;
  /** Business name, e.g. "DESSANGE — Bruxelles Marché Aux Herbes". */
  mainText: string;
  /** Address / location context, e.g. "Rue du Marché aux Herbes 74, Bruxelles, Belgique". */
  secondaryText: string;
}

/** Full details from GET /locations/place-details (Google Places Details). */
export interface PlaceDetails {
  placeId: string;
  name: string;
  formattedAddress: string;
  phone?: string;
  website?: string;
  latitude?: number;
  longitude?: number;
  /** ISO-3166-1 alpha-2, uppercase. */
  countryCode?: string;
}

// ---------------------------------------------------------------------------
// TargetWarning (D14) — non-blocking; returned by POST and stored in the row.
// ---------------------------------------------------------------------------

/**
 * Written at consolidation; never blocks the write.
 * NO_SIGNAL_PRODUCED is the critical one: the row exists and counts toward caps
 * but can never match anything — completely inert, not just degraded.
 */
export interface TargetWarning {
  kind:
    | 'GENERIC_VALUE'      // a common word or lone surname — the user's call
    | 'TEXT_TOO_LONG'      // > 8 tokens: stored, displayed, compiles NO text signal (§3.2)
    | 'NO_SIGNAL_PRODUCED'; // NOTHING matchable was compiled — the row is inert
  /** Absent for target-level kinds (NO_SIGNAL_PRODUCED when every field failed). */
  field?: string;
  /** Already in the brand's working language. */
  message: string;
}

// ---------------------------------------------------------------------------
// Target, TargetField, TargetInput — the UI-visible layers
// ---------------------------------------------------------------------------

/**
 * One user input on a Target. `type` comes from FIELD_REGISTRY, never from the request (D8).
 * A zone field embeds the resolved place (§2.4); all other fields are plain strings.
 */
export interface TargetField {
  name: string;
  value: string | TargetZone;
  /**
   * "This value is NOT unique to this établissement" — the user's own correction to the
   * uniqueness verdict (2026-09-23). When true the field is still STORED (the phone and
   * the URL are contact details, and the name is the label) but compiles to NO signal.
   *
   * One direction only, and that asymmetry is the whole design. The verdict itself is
   * never persisted: `TargetIndex._collision_mask` decides at index load, across the
   * whole partition, and re-decides on every cold load — so a stored "unique" would go
   * silently stale the day a second fiche gets the same switchboard number. What the
   * index CANNOT know is a value shared with établissements Traaker has never been told
   * about, and that is exactly what this flag records. The reverse — forcing a detected
   * collision back ON — is deliberately unrepresentable: the mask would suppress it
   * anyway, so the checkbox would be lying.
   */
  notUnique?: boolean;
  /**
   * `produit` URLs only — flags this URL as a buying/purchase page (2026-09-23).
   * Analytics metadata only: never a signal, never affects matching. Stored verbatim
   * so the frontend can show "🛒" badges and filter purchase-page citations separately.
   */
  buyingPage?: boolean;
}

/**
 * Business object the config UI edits.
 * Lives in its OWN shard at hash64(targetId) % shardCount — separate from its signals (D44).
 */
export interface Target {
  targetId: string;
  /** Reporting axis: 'own' | a competitor id (D7). */
  ownerId: string;
  category: TargetCategory;
  label: string;
  /** `marque` only. A palette token, never a signal, never in targetsHash (D45). */
  color?: TargetColorToken;
  /** Points to a `marque` target of the same ownerId (D7). Required for `etablissement`. */
  parentTargetId?: string;
  fields: TargetField[];
  /**
   * Tag IDS, resolved against `TargetsManifest.tags` (2026-09-23). Never a signal
   * (D45, as for `color`) — but UNLIKE `color` these ids are written into the `.idx`,
   * so ASSIGNING one DOES move `targetsHash`: the search Lambda's index cache is keyed
   * on it (D39), and without that move a warm Lambda serves the pre-tag index forever.
   * Renaming one does not move it, and needs no reindex.
   *
   * IDS, not names, so renaming a tag is ONE manifest write and is retroactive for
   * every cible already tagged. Every API surface speaks names — the writer resolves
   * name → id on save, readers resolve id → name — so this is the only place ids are
   * visible. ≤ `TARGET_LIMITS.maxTagsPerTarget`.
   */
  tags?: number[];
  /**
   * Links to `produit` / `collection` cibles of the SAME `ownerId` (2026-10-06).
   * Distinct from `parentTargetId`, which is reserved for the parent `marque`.
   * Never compiled into a signal — but, like `tags`, it IS part of `_shard_hash`,
   * so two cibles differing only by their relation set never collide on one shard key.
   * ≤ `TARGET_LIMITS.maxRelatedTargets`; over-cap is rejected, never truncated.
   */
  relatedTargetIds?: string[];
  /** Written at consolidation (D14); returned by GET /targets. */
  warnings?: TargetWarning[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Input for POST /brands/:brandId/targets.
 * Identity key is (ownerId, category, field, normalizedValue) — upsert semantics (D19).
 * Shape is validated in TypeScript (field exists in FIELD_REGISTRY, cardinality within cap,
 * ≤ TARGET_LIMITS.maxBatchRows / maxBatchBytes). Never a normalize_*, never a hash64 here.
 */
export interface TargetInput {
  ownerId: string;
  category: TargetCategory;
  label: string;
  parentTargetId?: string;
  fields: TargetField[];
  /**
   * Tag NAMES as typed, free-form (2026-09-23). The writer trims, dedupes
   * case-insensitively, and resolves each to an id — allocating one for a name the
   * brand has never used. Free-form on purpose: the manifest table is a dedup
   * registry, not a vocabulary anyone has to maintain.
   */
  tags?: string[];
  /**
   * `produit` / `collection` targetIds of the same `ownerId` (2026-10-06). Validated in
   * the API before dispatch — the worker only loads the partition it writes, so it
   * could not see the produit rows to check them.
   */
  relatedTargetIds?: string[];
}

// ---------------------------------------------------------------------------
// Signal — the only thing the matcher ever sees (§2.1)
// ---------------------------------------------------------------------------

/**
 * One normalized, matchable string (or proximity pair) derived from a Field.
 * The matcher never re-folds a stored value (D42). Values here are already folded.
 * `rootMarqueId` is denormalized at consolidation for rollups and colour resolution.
 */
export interface Signal {
  targetId: string;
  ownerId: string;
  /** Denormalized at consolidation. For rollups + colour resolution (D45, §3.5). */
  rootMarqueId?: string;
  category: TargetCategory;
  field: string;
  kind: 'text' | 'url' | 'pair';
  /** Already folded/normalized (§3.1). NEVER fold at match time. */
  value: string;
  /** Only for kind='pair'. */
  pair?: { a: string; b: string; window: number };
  tier: 'identifie' | 'probable';
}

// ---------------------------------------------------------------------------
// Manifest (§2.6)
// ---------------------------------------------------------------------------

export interface TargetsManifest {
  /** Hash over the sorted shard hashes. What the audit payload pins (D17). */
  targetsHash: string;
  /** The cap is on THIS (D13). */
  signalCount: number;
  targetCount: number;
  updatedAt: string;
  /** One entry per ownerId × category. shardCount is fixed per partition (D38). */
  partitions: {
    ownerId: string;
    category: TargetCategory;
    /** shardIndex = hash64(field, normalizedValue) % shardCount */
    shardCount: number;
  }[];
  shards: {
    ownerId: string;
    category: TargetCategory;
    /** The hash bucket (0..shardCount-1) — NOT a fill order (D38). */
    index: number;
    shardHash: string;
    rows: number;
    signals: number;
  }[];
  /**
   * Config-screen counters by parentTargetId × category.
   * Avoids reading shards just to display totals (D7).
   */
  countsByParentAndCategory: Record<string, Partial<Record<TargetCategory, number>>>;
  /**
   * Tag registry for this brand — the id → name map `Target.tags` points into
   * (2026-09-23). Lives here and not in the `.idx` so a RENAME is one manifest write
   * with no reindex and no target rewrite: the search Lambda already GETs the manifest
   * on every response (uncached, D17/D41), so resolved names are always current.
   *
   * `nextId` is never decremented — a deleted tag's id is retired, not reused, so a
   * cible that still carries it cannot silently inherit an unrelated name; an id with
   * no entry resolves to nothing and is dropped from the match rather than shown as a
   * number.
   */
  tags?: {
    nextId: number;
    entries: { id: number; name: string }[];
  };
  /**
   * Set in the SAME CAS write as the row that crossed 25 000.
   * Enqueue deduplicated (only if absent). NOT a write lock — writes continue until 2×. (D38)
   */
  reshardPending?: {
    ownerId: string;
    category: TargetCategory;
    requestedAt: string;
  }[];
  /**
   * Last reshard record so TARGETS_HASH_MISMATCH is attributable to a rehash, not a customer edit (D38).
   * Overwritten, not appended.
   */
  resharded?: {
    from: string;
    to: string;
    at: string;
  };
  /**
   * Present while an import is waiting or being consolidated (D37).
   * Blocks writes to EVERY category — the worker must win the manifest CAS.
   */
  importJob?: {
    importId: string;
    /**
     * absent = 'import'. A reshard takes the same lock but must show "index maintenance",
     * not "import en cours" (D38). `delta` / `delete` are a manual edit or a single-target
     * delete on a partition at or above `TARGET_LIMITS.asyncDeltaRows`, queued by the API
     * under this lock instead of run inside the request (2026-09-21).
     */
    kind?: 'import' | 'reshard' | 'delta' | 'delete';
    status: 'queued' | 'running';
    /** Advisory only — the real count needs the parts parsed (D37). */
    declaredRows?: number;
    queuedAt: string;
    startedAt?: string;
    heartbeatAt: string;
  };
  /**
   * Present only while establishment geocoding is queued or running (D36).
   * Blocks writes to `etablissement` only.
   */
  geocodeJob?: {
    importId: string;
    /** queued ≠ running: the worker is serialized; the wait is real. */
    status: 'queued' | 'running';
    rows: number;
    resolved: number;
    queuedAt: string;
    startedAt?: string;
    /** Stale → UI unlocks with a warning. The heartbeat is what keeps the lock from being permanent. */
    heartbeatAt: string;
  };
}

// ---------------------------------------------------------------------------
// Key check — "Unique à cet établissement ?" in the form (2026-09-23)
// ---------------------------------------------------------------------------

/**
 * Asks, for values the user is typing, whether each would identify THIS établissement
 * alone. Read-only and never stored: it renders `TargetIndex._collision_mask`'s verdict
 * BEFORE the save instead of after, so the user learns why a name will not count at the
 * moment they can still change it.
 *
 * ⚠ Answered against the UNMASKED signal rows, never through `search()`. `_probe` steps
 * over suppressed rows, so once two fiches share a value BOTH are suppressed and a
 * search returns nothing — the shared value would read back as unique, inverted in
 * exactly the case that matters.
 */
export interface TargetKeyCheckRequest {
  /** The fiche being edited, excluded so its own stored values are not self-conflicts. */
  targetId?: string;
  /**
   * The fiche's zone, needed to compile the candidates exactly as a save would: the phone
   * only resolves to E.164 with a `countryCode`, so without it a national-format number
   * compares against nothing and reads back as unique.
   */
  zone?: TargetZone;
  /** Batched: one invoke per dialog, not one per keystroke. */
  values: { field: string; value: string }[];
  /**
   * The two halves of the `marque + ville` rung, RAW — the server folds them, so
   * `_fold`/`_pair_half` stay the single implementation. `pairCity` is the fiche's
   * `zone.cityName` and nothing else (the compiler reads no other field for the city half),
   * `pairMarqueAliases` the parent marque's name plus its aliases, because the rung is
   * suppressed per (city, alias) group and not per city.
   *
   * Omit both to ask nothing: a fiche with no `cityName` gets no city rung, so there is
   * nothing to gate.
   */
  pairCity?: string;
  pairMarqueAliases?: string[];
}

export interface TargetKeyVerdict {
  field: string;
  /** Echoed VERBATIM as sent, so the caller can match verdicts back to its inputs. */
  value: string;
  unique: boolean;
  /**
   * Whether the value produces a signal at all — `false` for a value the compiler drops
   * (shorter than MIN_TEXT_LENGTH, an unnormalizable phone, a field the registry does not
   * know). Never a key, so the footer's "N clés" must not count it, and it is NOT the same
   * state as a collision: nothing is wrong with the value, it is just too thin to match on.
   */
  compiles: boolean;
  /**
   * What already carries it. `marque` is the bare-brand case (the answer named the
   * BRAND, whose own signal already counts it); `etablissement` means other branches.
   * Two different messages, because the fixes differ.
   *
   * No count of the conflicting fiches: a signal row's identity is
   * (ownerId, category, field, value) with no targetId (D19), so two fiches sharing a
   * value in the SAME field store ONE row and any count would read 1 however many share
   * it. The verdict is unaffected — one carrier answers "not unique".
   */
  conflict?: 'etablissement' | 'marque';
}

export interface TargetKeyCheckResult {
  /** The manifest the verdicts were computed against — a snapshot, by construction. */
  targetsHash: string;
  verdicts: TargetKeyVerdict[];
  /**
   * Does the `marque + ville` rung name exactly ONE branch? Answers the same question
   * `TargetIndex._pair_collision_mask` answers at index load, so the form can show the rung
   * as a key only where it will actually fire.
   *
   * ABSENT when the request asked nothing (no `pairCity`) — which is NOT the same as `false`:
   * absent means the fiche has no city rung at all, `false` means it has one and two branches
   * share it. Unlike `TargetKeyVerdict.unique`, this counts fiches exactly, because a pair
   * row's identity includes its `targetId`.
   */
  cityPairUnique?: boolean;
}

// ---------------------------------------------------------------------------
// Search API — §4.2
// ---------------------------------------------------------------------------

export interface TargetSearchRequest {
  tenantId: string;
  brandId: string;
  /** Optional; the manifest the audit was dispatched against (D17). */
  targetsHash?: string;
  text: string;
  /** POSITIONAL — index-aligned with the citation list. Never reorder or dedupe (§3.3). */
  urls: string[];
  /** Optional filter — omit for all owners (competitors included). */
  ownerIds?: string[];
  maxMatches?: number;
}

export interface TargetMatch {
  targetId: string;
  ownerId: string;
  rootMarqueId?: string;
  category: TargetCategory;
  /** Which field produced this match — provenance for the UI highlight and audit trail. */
  field: string;
  kind: 'text' | 'url' | 'pair';
  /**
   * The probe that matched — equal to the stored signal by construction (D12),
   * and FOLDED (D42: accents dropped, lowercased, whitespace collapsed).
   * `'a|b'` for a `pair`, the shape the signal itself carries.
   * This is what the UI highlights from, not `position` — but a pair is NOT two
   * highlightable terms: half `b` is a marque alias the marque's own signal already
   * covers, and half `a` is only paintable where `b` is within `window` of it. See
   * `TargetHighlight.pairWith`.
   */
  value: string;
  tier: 'identifie' | 'probable';
  /** Found in the answer body, or in a cited source. */
  via: 'text' | 'source';
  /**
   * The matched cible's tag NAMES, resolved from the manifest registry (2026-09-23).
   * Attached to the target, so identical for `via: 'text'` and `via: 'source'`.
   * Omitted when the cible has none. For downstream analytics only — tags never
   * take part in matching.
   */
  tags?: string[];
  /**
   * Char offset in the FOLDED answer (real offsets since 2026-09-22, previously
   * token index × 6), and `null` on a url match. ⚠ Not usable for highlighting: the
   * fold is not length-preserving, the frontend markdown renderer rewrites the
   * answer before rendering it, and the dedup keeps only the first occurrence per
   * (targetId, field, kind, sourceIndex). Highlight by `value`.
   */
  position?: number;
  /**
   * `kind: 'pair'` only — the max characters BETWEEN the two halves that this pair was
   * matched at, so a consumer can REPRODUCE the co-occurrence test instead of trusting
   * a half on its own. It is per-rung of the marque + lieu ladder, not a constant: 80
   * for a street pair, 25 for a city pair (§11.1). This is what
   * `TargetHighlight.window` is built from.
   */
  window?: number;
  /** POSITIONAL index into `urls` (§3.3) — the FIRST source this signal matched. */
  sourceIndex?: number;
  /**
   * `via: 'source'` only — EVERY index into `urls` this signal matched, in order, the
   * first being `sourceIndex` (2026-11-02). Still ONE match per signal, so a target cited
   * by two links is one citation for every counter; this list is for colouring chips.
   */
  sourceIndexes?: number[];
  /**
   * Pre-computed so consumers get "top targets" without a re-scan. Since 2026-11-02 the
   * matches (and so these ranks, and the `maxMatches` cap) follow FIRST APPEARANCE: body
   * matches by `position`, then source matches by `sourceIndex`; before, a uuid order.
   */
  rankInOwner: number;
  rankGlobal: number;
}

/**
 * One matched term, persisted on a BP result row so the UI can highlight it
 * without re-running the matcher (Lot 6 step 4).
 *
 * TERMS, not spans — see `TargetMatch.position` for why offsets can't survive the
 * render pipeline. The consumer re-finds every occurrence itself with the same
 * accent fold it already applies to the brand name.
 */
export interface TargetHighlight {
  /** Folded (D42), so match against a folded copy of the answer. */
  value: string;
  targetId: string;
  /** Resolve the highlight colour off this marque's `color` (D45). Absent when the
   *  matched signal declared no parent. */
  rootMarqueId?: string;
  /**
   * Set only for a PAIR signal, and when set the highlight is CONDITIONAL: paint
   * `value` only where `pairWith` also occurs within `window` characters of it.
   * Absent on every other kind, which highlights unconditionally.
   *
   * A pair means "this street/city, NEXT TO this marque" — neither half identifies
   * anything alone, which is exactly why the matcher scores them together. Until
   * 2026-09-23 the two halves were shipped as two independent entries, so a fiche on
   * Avenue Louise painted the word "Louise" wherever it appeared — including in a
   * COMPETITOR's address in the same answer, in the client's own brand colour. The
   * competitor was highlighted as if it were the brand's branch.
   *
   * `pairWith` is the marque alias (half `b`) and `value` is the distinguishing half
   * `a`. Only `a` is emitted: half `b` is by construction a marque alias, so the
   * marque's own name signal already highlights it, with the same colour (D45).
   */
  pairWith?: string;
  /**
   * Max characters BETWEEN the two halves — the matcher's `_gap`, not a
   * start-to-start distance (see targets.md §11.1). It differs per rung: a street
   * pair carries 80, a city pair 25, and the consumer must use the value shipped here
   * rather than a constant of its own, or it re-decides a gate the index already
   * settled.
   */
  window?: number;
}

/**
 * One matched CITATION, persisted on a BP result row so the UI can colour the
 * source chip a target was found in.
 *
 * The counterpart to `TargetHighlight`: a `via: 'source'` match is a real hit that
 * simply isn't in the prose, so it colours the source instead of the answer text.
 */
export interface TargetSourceHighlight {
  /** Index into the row's `sources` (and `sourceLinks`) — the same positional list
   *  inline `[n]` markers point into. */
  sourceIndex: number;
  targetId: string;
  /** Resolve the chip colour off this marque's `color` (D45). Absent when the
   *  matched signal declared no parent. */
  rootMarqueId?: string;
}

export interface TargetSearchResult {
  /** The hash that was ACTUALLY used — surfaces a drift when a mismatch occurs (D17, D41). */
  targetsHash: string;
  /** Matches before the cap. */
  total: number;
  totalsByOwner: Record<string, number>;
  totalsByCategory: Partial<Record<TargetCategory, number>>;
  truncated: boolean;
  matches: TargetMatch[];
  /** e.g. TARGETS_HASH_MISMATCH, TARGETS_RESHARD_REQUIRED. */
  warnings?: string[];
}

// ---------------------------------------------------------------------------
// FIELD_REGISTRY — single source of truth for what a field is (D8, §2.3)
// ---------------------------------------------------------------------------

export type FieldType =
  | 'text'
  | 'url'
  | 'zone'
  | 'phone'
  | 'email'
  | 'address'
  | 'placeid'
  /**
   * Stored on personne targets for form re-population; compiles NO signal (like `zone`/`address`).
   * The accepted name variants are stored as `alias` fields by the frontend after generation.
   */
  | 'titlecode'
  /**
   * Stored on personne targets (firstName, lastName) for form re-population; compiles NO signal.
   * Signals come entirely from the `alias` list the frontend builds from title+firstName+lastName.
   */
  | 'givenname'
  /**
   * Closed vocabulary stored for grouping/filtering; compiles NO signal.
   * The accepted values live in the field's `values` list and are rejected on write.
   */
  | 'enum'
  /** `YYYY-MM-DD` calendar date stored for sorting/filtering; compiles NO signal. */
  | 'date';

/** Accepted values for `contenu.contentType` (2026-10-06). FR-facing labels live in i18n. */
export const CONTENU_CONTENT_TYPES = [
  // Content formats — manually chosen by the user
  'article',
  'blog',
  'newsletter',
  'podcast',
  'guide',
  'tutorial',
  'benchmark',
  'presse',
  // Platform types — auto-derived server-side from the url host (normalize.py)
  'linkedin',
  'youtube',
  'instagram',
  'x',
  'tiktok',
  'facebook',
  'autre',
] as const;
export type ContenuContentType = (typeof CONTENU_CONTENT_TYPES)[number];

export interface FieldDef {
  type: FieldType;
  /** Maximum number of values for this field. Absent = 1. */
  maxCount?: number;
  /** Minimum character length for text signals. Default: 4 (3 with digit/hyphen). */
  minLength?: number;
  /** True if this field is required on the target (parentTargetId is required for etablissement). */
  required?: boolean;
  /** `enum` fields only — the closed vocabulary. Any other value is rejected on write. */
  values?: readonly string[];
}

/**
 * One entry per (category, fieldName). The compiler's walk over this is the whole match registry.
 * Adding a field = one row here + one compiler branch — never a new matcher.
 * Admission rule (D29): a field exists only if it produces a signal, or builds one.
 */
export const FIELD_REGISTRY: Record<TargetCategory, Record<string, FieldDef>> = {
  marque: {
    /** Reproducible-id key (docs/features/targets.md §20.1). Stored only — NO signal, never in the `.idx`. */
    id:     { type: 'givenname' },
    name:   { type: 'text' },
    alias:  { type: 'text', maxCount: 10 }, // mirrors BRAND_STRATEGY_LIMITS.aliases
    domain: { type: 'url',  maxCount: 10 },
  },
  offre: {
    /** Reproducible-id key (docs/features/targets.md §20.1). Stored only — NO signal, never in the `.idx`. */
    id:    { type: 'givenname' },
    name:  { type: 'text' },
    alias: { type: 'text', maxCount: 5 },
    code:  { type: 'text' },
    url:   { type: 'url' },
  },
  produit: {
    /**
     * Unique product identifier (GTIN, SKU, internal ref). Required on import.
     * Stored for display and deduplication only — produces NO signal (2026-09-23
     * redesign: signals are URLs exclusively). Replaces the separate `sku`/`gtin` fields.
     */
    id:   { type: 'givenname' },
    /**
     * Product display name. Optional, produces NO signal. Shown in the list view
     * but never matched against AI responses — only URLs are signals for `produit`.
     */
    name: { type: 'givenname' },
    /**
     * One URL per product page. A product can have up to `TARGET_LIMITS.maxProduitUrls`
     * URLs (main page, mobile, regional variants…). Each compiles to a URL signal.
     * Each field entry may carry `buyingPage: true` (analytics, ignored by compiler).
     */
    url:  { type: 'url', maxCount: 20 },
  },
  collection: {
    /** Optional unique id — merge key on re-import (no signal, like produit.id). */
    id:   { type: 'givenname' },
    url:  { type: 'url' },         // category/section URL prefix (e.g. acme.fr/c/accessoires/)
    /**
     * Display name. Stored for display only — produces NO signal.
     * (Fixed 2026-09-23: was `type: 'text'`, which incorrectly made collection names
     * matchable against AI responses. Only URLs are signals for `collection`.)
     */
    name: { type: 'givenname' },
  },
  contenu: {
    /** Reproducible-id key (docs/features/targets.md §20.1). Stored only — NO signal, never in the `.idx`. */
    id:    { type: 'givenname' },
    title: { type: 'text' },
    url:   { type: 'url' },
    /**
     * Publication surface. Produces NO signal — it groups the content view (2026-10-06).
     * Derived server-side from the url's host when the caller omits it; a value the
     * caller DID supply is never re-derived (see `derive_content_type` in normalize.py).
     */
    contentType: { type: 'enum', values: CONTENU_CONTENT_TYPES },
    /** Publication date, `YYYY-MM-DD`. Produces NO signal — sorting/freshness only. */
    publishedAt: { type: 'date' },
  },
  etablissement: {
    /** Reproducible-id key (docs/features/targets.md §20.1). Stored only — NO signal, never in the `.idx`. */
    id:      { type: 'givenname' },
    name:    { type: 'text' },
    alias:   { type: 'text', maxCount: 3 }, // mirrors TARGET_LIMITS.maxEtablissementAliases
    zone:    { type: 'zone', required: true },
    url:     { type: 'url' },
    phone:   { type: 'phone' },
    address: { type: 'address' },
    placeId: { type: 'placeid' },
  },
  personne: {
    /** Reproducible-id key (docs/features/targets.md §20.1) — disambiguates two homonyms.
     *  Stored only — NO signal, never in the `.idx`. */
    id:        { type: 'givenname' },
    /**
     * Title code stored for form re-population (e.g. "Dr"). Produces NO direct signal.
     * The frontend generates name combinations from title+firstName+lastName and stores
     * the accepted forms as `alias` values. (2026-09-23 refonte — replaces `fullName`.)
     */
    title:     { type: 'titlecode' },
    /**
     * Given name stored for form re-population. Produces NO direct signal.
     * See `title` for the generation model.
     */
    firstName: { type: 'givenname' },
    /**
     * Family name stored for form re-population. Produces NO direct signal.
     * (Was previously `type: 'text'` with a lone-surname `tier: probable` signal;
     * signals now come entirely from `alias` so the lone-name form is opt-in at
     * the checkbox level — off by default.)
     */
    lastName:  { type: 'givenname' },
    /** All accepted name forms chosen by the user. Each becomes a `text` signal. */
    alias:     { type: 'text', maxCount: 20 },
    email:     { type: 'email' },
    phone:     { type: 'phone' },
  },
} as const;

// ---------------------------------------------------------------------------
// TARGET_LIMITS — single source of truth for API caps (§4.1, D13, D19)
// Import from @traaker/shared; NEVER re-declare in a component (BRAND_STRATEGY_LIMITS lesson).
// ---------------------------------------------------------------------------

export const TARGET_LIMITS = {
  /** Max rows AND max bytes per synchronous batch — whichever binds first (D19). */
  maxBatchRows: 5_000,
  maxBatchBytes: 1_048_576, // 1 MB

  /**
   * Partition row count (Target + Signal rows, summed over its shard entries) at or above
   * which a manual upsert or single-target delete is QUEUED on the consolidate queue under
   * the `importJob` lock (kind `delta` / `delete`) and answered 202, instead of run inside
   * the request (2026-09-21). `apply_delta` and `delete_target` read and re-serialize the
   * whole partition, measured at ~2.5 s per 100k rows on Lambda, so beyond this a
   * synchronous save straddles the 25 s invoke deadline.
   */
  asyncDeltaRows: 100_000,

  /** Soft shard row cap — crossing it queues a reshard (D38). */
  shardRowSoftCap: 25_000,

  /**
   * Per-brand `contenu` cap (docs/features/targets.md §20.5). A TECHNICAL blocker, not a quota:
   * it bounds the dashboard's whole-partition content read. Not in the metering registry,
   * not in `resolveLimits`, never shown as an allowance. A platform admin lifts it per
   * tenant through `TenantSettings.targetLimitOverrides.maxContenusPerBrand`, clamped to
   * `maxContenusPerBrandCeiling`. Mirrored in Python (`store.MAX_CONTENUS_PER_BRAND`).
   */
  maxContenusPerBrand: 10_000,
  /** Engineering ceiling on the override — what the content reader serves under the 29 s
   *  API limit (~2.5 s per 100k rows, 3 rows per content). Past it: the deferred side-file. */
  maxContenusPerBrandCeiling: 100_000,

  /** Max ids per by-id lookup (§5.1) — keeps it from becoming a whole-partition read. */
  maxTargetsByIdLookup: 200,

  /**
   * Hard shard row cap (2× soft) — write refused at this point (D38).
   * Two different HTTP answers at this boundary: 409 with job state if reshard pending+heartbeating,
   * TARGETS_RESHARD_REQUIRED otherwise.
   */
  shardRowHardCap: 50_000,

  /** Maximum shards per partition (D38). Every shard is one .idx GET on cold start. */
  maxShardsPerPartition: 64,

  /** Target shard rows after a reshard (D38). */
  reshardTargetRowsPerShard: 5_000,

  /** Reshard growth factor (D38). newShardCount >= 4 * currentShardCount. */
  reshardMinGrowthFactor: 4,

  /** Default maxMatches for a search request (§4.2). */
  defaultMaxMatches: 200,

  /** Maximum token count for a text signal (§3.2). Above this: TEXT_TOO_LONG warning, no text signal. */
  maxTextTokens: 8,

  /** Minimum character length for a pure-alpha text signal (§3.1). */
  minTextLength: 4,

  /** Minimum length when the value contains a digit or hyphen (SKUs like X-12) (§3.1). */
  minTextLengthWithDigitOrHyphen: 3,

  /** Maximum aliases per marque (mirrors BRAND_STRATEGY_LIMITS.aliases). */
  maxMarqueAliases: 10,

  /**
   * Pair window, in REAL characters of the folded answer text, between the two
   * halves of an établissement pair (§2.5). Mirrors `PAIR_WINDOW` in
   * packages/agent-targets/app/targets/compile.py — was 60 here and 30 there
   * until 2026-09-22, then 30 until 2026-09-23.
   *
   * 80 because there is now exactly ONE pair left — marque + rue — and a street is
   * specific enough to afford the room: the real answer "Le coiffeur DESSANGE est
   * idéalement situé au cœur de Bruxelles, rue du Marché aux Herbes" puts the halves
   * 52 characters apart and was missed at 30. The city and country pairs, which
   * could NOT afford it, no longer exist (§11.1).
   */
  pairWindowChars: 80,

  /**
   * Minimum length of a pair half, spaces excluded. Lower than `minTextLength`
   * because the marque alias is always the other half. Mirrors
   * `MIN_PAIR_HALF_LENGTH` in compile.py.
   */
  minPairHalfLength: 3,

  /** Maximum URL fields per produit target (2026-09-23). One product, multiple pages/variants. */
  maxProduitUrls: 20,

  /** Maximum aliases per établissement (2026-09-23). Own name shape, so not the marque cap. */
  maxEtablissementAliases: 3,

  /**
   * Maximum alias entries per personne (2026-09-23). Bumped from 5 to hold all generated
   * name combinations across multiple languages (title × 3 combos × N lang forms + sans titre).
   */
  maxPersonneAliases: 20,

  /** Maximum tags per cible, any category (2026-09-23). Analytics labels, never matched. */
  maxTagsPerTarget: 20,

  /** Maximum characters of a single tag name, after trim. */
  maxTagLength: 40,

  /**
   * Maximum `relatedTargetIds` per cible (2026-10-06). Over-cap is REJECTED, never
   * truncated — a silently dropped product link reads as "this content sells nothing".
   */
  maxRelatedTargets: 20,

  /** Per-request geo autocomplete minimum query length — below this the controller returns 422. */
  geoAutocompleteMinLength: 4,
} as const;

// ---------------------------------------------------------------------------
// Denormalized summary stored on the brand row (§5.1, Lot 5)
// Avoids an S3 read just to render the GeoStrategy card and the dashboard.
// ---------------------------------------------------------------------------

export interface TargetsMeta {
  targetsHash: string;
  signalCount: number;
  targetCount: number;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// By-id lookup (docs/features/targets.md §20.7) — `POST /brands/:brandId/targets/by-id`
// ---------------------------------------------------------------------------

export interface TargetsByIdRequest {
  /** Defaults to `'own'`. */
  ownerId?: string;
  /** ≤ `TARGET_LIMITS.maxTargetsByIdLookup`. `category` when known saves GETs. */
  ids: Array<{ targetId: string; category?: TargetCategory }>;
}

/** Display fields only. `label` is the row's name/title/label — ABSENT when it has none
 *  (the UI then shows `url`, never an invented name). */
export interface TargetByIdItem {
  targetId: string;
  category: TargetCategory;
  label?: string;
  url?: string;
  contentType?: ContenuContentType;
  publishedAt?: string;
  relatedTargetIds?: string[];
}

export interface TargetsByIdResponse {
  items: TargetByIdItem[];
  /** Ids that resolved to nothing — rendered « Cible non résolue », never dropped. */
  notFound: string[];
}
